import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Button, Input, Alert, Select } from '@components';
import {
  TopUpProduct,
  TopUpProductType,
  PaymentMethod,
  DirectChargeResponse,
  CouponValidationResponse,
} from '../types';
import { loadStripe } from '@stripe/stripe-js';

interface TopUpProps {
  onTopUp: (
    productType: TopUpProductType,
    paymentMethodId: string,
    couponCode?: string,
    finalAmount?: string
  ) => Promise<DirectChargeResponse>;
  onValidateCoupon: (couponCode: string, amount: string) => Promise<CouponValidationResponse>;
  paymentMethods: PaymentMethod[];
  loading?: boolean;
  error?: string | null;
  onClearError?: () => void;
  onSuccess?: () => void | Promise<void>;
}

const stripePromise = loadStripe(
  (import.meta as { env?: { VITE_STRIPE_PUBLISHABLE_KEY?: string } }).env
    ?.VITE_STRIPE_PUBLISHABLE_KEY || ''
);

const TOP_UP_PRODUCTS: TopUpProduct[] = [
  { type: 'topup_50', amount: 50, label: '€50 Top-Up' },
  { type: 'topup_100', amount: 100, label: '€100 Top-Up' },
  { type: 'topup_150', amount: 150, label: '€150 Top-Up' },
  { type: 'topup_300', amount: 300, label: '€300 Top-Up' },
  { type: 'topup_500', amount: 500, label: '€500 Top-Up' },
];

const ERROR_AUTO_DISMISS_TIME = 5000;

export const TopUp: React.FC<TopUpProps> = ({
  onTopUp,
  onValidateCoupon,
  paymentMethods = [],
  loading = false,
  error = null,
  onClearError,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [selectedProduct, setSelectedProduct] = useState<TopUpProductType | null>(null);
  const [couponCode, setCouponCode] = useState('');
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<string>('');
  const [showError, setShowError] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [couponValidating, setCouponValidating] = useState(false);
  const [couponValid, setCouponValid] = useState<boolean | null>(null);
  const [couponError, setCouponError] = useState<string | null>(null);
  const [discountData, setDiscountData] = useState<{
    originalAmount: string;
    discountAmount: string;
    finalAmount: string;
    discountType: 'fixed' | 'percentage';
    discountValue: string;
  } | null>(null);
  const [paymentDetails, setPaymentDetails] = useState<{
    baseAmount: string;
    taxAmount: string;
    finalAmount: string;
  } | null>(null);

  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const capitalizeBrand = (brand?: string) => {
    if (!brand) return 'Card';
    return brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();
  };

  const safePaymentMethods = useMemo(
    () => (Array.isArray(paymentMethods) ? paymentMethods : []),
    [paymentMethods]
  );

  const activePaymentMethods = useMemo(
    () => safePaymentMethods.filter(pm => pm.is_active),
    [safePaymentMethods]
  );

  useEffect(() => {
    if (safePaymentMethods.length > 0 && !selectedPaymentMethodId) {
      const defaultMethod = safePaymentMethods.find(pm => pm.is_default && pm.is_active);
      if (defaultMethod) {
        setSelectedPaymentMethodId(defaultMethod.stripe_payment_method_id);
      } else {
        const firstActive = safePaymentMethods.find(pm => pm.is_active);
        if (firstActive) {
          setSelectedPaymentMethodId(firstActive.stripe_payment_method_id);
        }
      }
    }
  }, [safePaymentMethods, selectedPaymentMethodId]);

  useEffect(() => {
    if (error) {
      setShowError(true);
      const timer = setTimeout(() => {
        setShowError(false);
      }, ERROR_AUTO_DISMISS_TIME);
      return () => clearTimeout(timer);
    } else {
      setShowError(false);
    }
  }, [error]);

  useEffect(() => {
    if (loading) {
      setShowError(false);
    }
  }, [loading]);

  const handleDismissError = () => {
    setShowError(false);
    onClearError?.();
  };

  const handleTopUp = async () => {
    if (!selectedProduct) {
      return;
    }
    if (!selectedPaymentMethodId) {
      setShowError(true);
      setLocalError('Please select a payment method');
      return;
    }

    setShowError(false);
    setLocalError(null);
    setSuccess(false);
    setLocalLoading(true);

    try {
      const result = await onTopUp(
        selectedProduct,
        selectedPaymentMethodId,
        couponCode || undefined
      );

      if (result.requires_action && result.client_secret) {
        const stripe = await stripePromise;
        if (!stripe) {
          throw new Error('Failed to load Stripe');
        }

        const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
          result.client_secret
        );

        if (stripeError) {
          throw new Error(stripeError.message || '3D Secure authentication failed');
        }

        if (paymentIntent?.status === 'succeeded') {
          setPaymentDetails({
            baseAmount: result.base_amount,
            taxAmount: result.tax_amount,
            finalAmount: result.final_amount,
          });
          setSuccess(true);
          setLocalLoading(false);
          await onSuccess?.();
          setTimeout(() => {
            setSuccess(false);
            setSelectedProduct(null);
            setCouponCode('');
            setCouponValid(null);
            setCouponError(null);
            setDiscountData(null);
            setPaymentDetails(null);
          }, 5000);
          return;
        }
      }

      if (result.status === 'succeeded') {
        setPaymentDetails({
          baseAmount: result.base_amount,
          taxAmount: result.tax_amount,
          finalAmount: result.final_amount,
        });
        setSuccess(true);
        setLocalLoading(false);
        await onSuccess?.();
        setTimeout(() => {
          setSuccess(false);
          setSelectedProduct(null);
          setCouponCode('');
          setCouponValid(null);
          setCouponError(null);
          setDiscountData(null);
          setPaymentDetails(null);
        }, 5000);
        return;
      }

      throw new Error(`Payment status: ${result.status}`);
    } catch (err) {
      setLocalLoading(false);
      const errorMessage = err instanceof Error ? err.message : 'Payment failed';
      setLocalError(errorMessage);
      setShowError(true);
    }
  };

  const selectedProductData = selectedProduct
    ? TOP_UP_PRODUCTS.find(p => p.type === selectedProduct)
    : null;

  const getProductAmount = useCallback(() => {
    return selectedProductData?.amount.toString() || '0';
  }, [selectedProductData]);

  const validateCouponCode = useCallback(
    async (code: string, amount: string) => {
      if (!code || code.trim().length === 0) {
        setCouponValid(null);
        setCouponError(null);
        setDiscountData(null);
        return;
      }

      setCouponValidating(true);
      setCouponError(null);

      try {
        const result = await onValidateCoupon(code.trim(), amount);

        if (result.valid) {
          setCouponValid(true);
          setCouponError(null);
          setDiscountData({
            originalAmount: result.original_amount!,
            discountAmount: result.discount_amount!,
            finalAmount: result.final_amount!,
            discountType: result.discount_type!,
            discountValue: result.discount_value!,
          });
        } else {
          setCouponValid(false);
          let errorMessage = 'Invalid coupon code';

          if (result.error) {
            if (typeof result.error === 'string') {
              errorMessage = result.error;
            } else if (Array.isArray(result.error)) {
              const firstError = result.error[0];
              if (typeof firstError === 'string') {
                errorMessage = firstError;
              } else if (firstError && typeof firstError === 'object' && 'string' in firstError) {
                errorMessage = (firstError as { string: string }).string;
              }
            }
          }

          setCouponError(errorMessage);
          setDiscountData(null);
        }
      } catch (err) {
        setCouponValid(false);
        setCouponError('Failed to validate coupon');
        setDiscountData(null);
      } finally {
        setCouponValidating(false);
      }
    },
    [onValidateCoupon]
  );

  const debouncedValidation = useCallback(
    (code: string, amount: string) => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }

      validationTimeoutRef.current = setTimeout(() => {
        validateCouponCode(code, amount);
      }, 500);
    },
    [validateCouponCode]
  );

  const handleCouponChange = (value: string) => {
    const uppercaseValue = value.toUpperCase();
    setCouponCode(uppercaseValue);
    setCouponValid(null);
    setCouponError(null);
    setDiscountData(null);

    if (selectedProductData) {
      debouncedValidation(uppercaseValue, getProductAmount());
    }
  };

  useEffect(() => {
    if (couponCode && selectedProductData && couponValid === true) {
      debouncedValidation(couponCode, getProductAmount());
    }
  }, [
    selectedProduct,
    couponCode,
    couponValid,
    selectedProductData,
    debouncedValidation,
    getProductAmount,
  ]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 id="topup-heading" className="text-xl font-semibold text-gray-900 dark:text-white">
            {t('billing.topUpBalance')}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            {t('billing.lowBalanceWarning')}
          </p>
        </div>
      </div>

      {(error || localError) && showError && (
        <Alert variant="error" title="Payment Error" dismissible onDismiss={handleDismissError}>
          {localError || error}
        </Alert>
      )}

      <Card>
        <CardContent className="p-6">
          <div className="space-y-6">
            <div>
              <label
                htmlFor="amount-selection"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3"
              >
                {t('billing.selectAmount')}
              </label>
              <div
                id="amount-selection"
                className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3"
                role="group"
                aria-labelledby="amount-selection"
              >
                {TOP_UP_PRODUCTS.map(product => (
                  <button
                    key={product.type}
                    type="button"
                    onClick={() => setSelectedProduct(product.type)}
                    disabled={loading}
                    aria-pressed={selectedProduct === product.type}
                    className={`
                      p-4 rounded-lg border-2 transition-all
                      ${
                        selectedProduct === product.type
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700'
                      }
                      ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    `}
                  >
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">
                        €{product.amount}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {product.label}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label
                htmlFor="payment-method"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('billing.paymentMethod')} *
              </label>
              {activePaymentMethods.length === 0 ? (
                <Alert variant="warning" title={t('billing.noPaymentMethods')}>
                  {t('billing.pleaseAddPaymentMethod')}
                </Alert>
              ) : (
                <Select
                  id="payment-method"
                  value={selectedPaymentMethodId}
                  onChange={value => {
                    const stringValue = Array.isArray(value) ? value[0] : value;
                    setSelectedPaymentMethodId(stringValue);
                  }}
                  disabled={loading}
                  options={activePaymentMethods.map(pm => ({
                    value: pm.stripe_payment_method_id,
                    label: `${capitalizeBrand(pm.brand || pm.card_brand)} ${t('billing.cardEndingIn')} ${pm.last4 || pm.card_last4 || '****'} ${
                      pm.is_default ? `(${t('billing.default')})` : ''
                    }`,
                  }))}
                  className="w-full"
                />
              )}
            </div>

            <div>
              <label
                htmlFor="coupon-code"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                {t('billing.couponCodeOptional')}
              </label>
              <div className="relative">
                <Input
                  id="coupon-code"
                  type="text"
                  placeholder={t('billing.enterCouponCode')}
                  value={couponCode}
                  onChange={e => handleCouponChange(e.target.value)}
                  disabled={loading || !selectedProduct}
                  className={`w-full ${
                    couponValid === true
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : couponValid === false
                        ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                        : ''
                  }`}
                />
                {couponValidating && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <span className="material-icons animate-spin text-sm text-gray-400">
                      refresh
                    </span>
                  </div>
                )}
                {couponValid === true && !couponValidating && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <span className="material-icons text-sm text-green-600">check_circle</span>
                  </div>
                )}
                {couponValid === false && !couponValidating && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <span className="material-icons text-sm text-red-600">error</span>
                  </div>
                )}
              </div>
              {couponError && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{couponError}</p>
              )}
              {couponValid && !couponValidating && (
                <p className="mt-1 text-sm text-green-600 dark:text-green-400">
                  {t('billing.couponApplied')}
                </p>
              )}
            </div>

            {discountData && (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      {t('billing.originalAmount')}:
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 line-through">
                      €{discountData.originalAmount}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-green-600 dark:text-green-400 font-medium">
                      {t('billing.discount')}{' '}
                      {discountData.discountType === 'percentage'
                        ? `(${discountData.discountValue}%)`
                        : ''}
                      :
                    </span>
                    <span className="text-green-600 dark:text-green-400 font-medium">
                      -€{discountData.discountAmount}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-base pt-2 border-t border-green-200 dark:border-green-800">
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {t('billing.finalAmount')}:
                    </span>
                    <span className="font-bold text-lg text-primary-600 dark:text-primary-400">
                      €{discountData.finalAmount}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {!discountData && selectedProductData && (
              <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {t('billing.transactionAmount')}:
                  </span>
                  <span className="text-lg font-bold text-gray-900 dark:text-white">
                    €{selectedProductData.amount.toFixed(2)}
                  </span>
                </div>
              </div>
            )}

            {success && paymentDetails && (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      {t('billing.baseAmount')}:
                    </span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      €{parseFloat(paymentDetails.baseAmount).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">{t('billing.tax')}:</span>
                    <span className="text-gray-900 dark:text-white font-medium">
                      €{parseFloat(paymentDetails.taxAmount).toFixed(2)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-base pt-2 border-t border-green-200 dark:border-green-800">
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {t('billing.total')}:
                    </span>
                    <span className="font-bold text-lg text-primary-600 dark:text-primary-400">
                      €{parseFloat(paymentDetails.finalAmount).toFixed(2)}
                    </span>
                  </div>
                  <div className="pt-2 text-xs text-gray-500 dark:text-gray-400">
                    {t('billing.walletCreditedWithBaseAmount')}
                  </div>
                </div>
              </div>
            )}

            <Button
              variant="gradient-multi"
              onClick={handleTopUp}
              disabled={
                !selectedProduct ||
                !selectedPaymentMethodId ||
                activePaymentMethods.length === 0 ||
                loading ||
                localLoading ||
                success ||
                couponValidating
              }
              className="w-full flex items-center justify-center gap-2"
            >
              {loading || localLoading ? (
                <>
                  <span className="material-icons animate-spin">refresh</span>
                  {t('billing.processingPayment')}
                </>
              ) : success ? (
                <>
                  <span className="material-icons">check_circle</span>
                  {t('billing.paymentSuccessful')}
                </>
              ) : (
                <>
                  <span className="material-icons">payment</span>
                  {t('billing.charge')} €
                  {discountData ? discountData.finalAmount : selectedProductData?.amount || '0'}
                </>
              )}
            </Button>

            <p className="text-xs text-center text-gray-500 dark:text-gray-400">
              {t('billing.cardWillBeChargedImmediately')}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
